// Memory only, short-lived, bounded, and keyed by the authenticated session.
// Cache JSON text so callers that sort/mutate chart data never affect one another.
export const createRequestCache = ({ ttl = 30000, maxEntries = 64, maxBytes = 8 * 1024 * 1024,
  maxEntryBytes = 1024 * 1024, now = Date.now } = {}) => {
  const entries = new Map();
  const pending = new Map();
  let bytes = 0;
  let generation = 0;
  return {
    clear() {
      generation += 1;
      entries.clear();
      pending.clear();
      bytes = 0;
    },
    async get(key, load) {
      const existing = entries.get(key);
      if (existing && existing.expires > now()) {
        entries.delete(key);
        entries.set(key, existing);
        return JSON.parse(existing.text);
      }
      if (existing) {
        entries.delete(key);
        bytes -= existing.bytes;
      }
      let request = pending.get(key);
      if (!request) {
        const started = generation;
        request = Promise.resolve().then(load).then((value) => {
          const text = JSON.stringify(value);
          if (text === undefined) return { value };
          const size = text.length * 2;
          if (started === generation && size <= maxEntryBytes && size <= maxBytes) {
            while (entries.size && (bytes + size > maxBytes || entries.size >= maxEntries)) {
              const oldest = entries.keys().next().value;
              bytes -= entries.get(oldest).bytes;
              entries.delete(oldest);
            }
            entries.set(key, { text, bytes: size, expires: now() + ttl });
            bytes += size;
          }
          return { text };
        }).finally(() => {
          if (pending.get(key) === request) pending.delete(key);
        });
        pending.set(key, request);
      }
      const result = await request;
      return result.text === undefined ? result.value : JSON.parse(result.text);
    },
  };
};

export const pageRequestCache = createRequestCache();
export const clearPageRequestCache = () => pageRequestCache.clear();
